import { Formik } from 'formik';
import React, { useContext, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import * as Yup from 'yup';
import Button from '../buttons/Button';
import PhotoUploader from '../fileUploads/PhotoUploader';
import PoiTypeIcon from '../icons/PoiTypeIcon';
import Checkbox from '../inputs/Checkbox';
import SelectInput from '../inputs/SelectInput';
import MapLocationPicker from '../maps/MapLocationPicker';
import CoordinatesModal from '../modals/CoordinatesModal';
import FormikInputField from 'components/forms/fields/FormikInputField';
import { Send } from 'react-feather';
import AppContext from 'contexts/AppContext';

const initialValues = {
    name: '',
    type_id: '',
    location: {
        lat: '',
        lng: ''
    },
    no_photos: false
};

const schema = Yup.object().shape({
    name: Yup.string().required('Ime izletne točke je obvezno.'),
    type_id: Yup.string().required('Izberi tip izletne točke'),
    location: Yup.object().shape({
        lat: Yup.number(),
        lng: Yup.number()
    }),
    no_photos: Yup.bool()
});

function PoiTypeOptionLabel({ slug, name }) {
    return (
        <span className="inline-flex items-center gap-3">
            <PoiTypeIcon slug={slug} size={20} />
            <span>{name}</span>
        </span>
    );
}

function PoiSuggestionForm({ onSubmit }) {
    const { data: poiTypes } = useSWR('/public/poi-types');
    const [isCoordinatesModalActive, setIsCoordinatesModalActive] = useState(false);
    const [modalCoordinates, setModalCoordinates] = useState();
    const [files, setFiles] = useState([]);
    const [thumbs, setThumbs] = useState([]);
    const { state } = useContext(AppContext);
    const contestState = state.photoContest.state;

    const filesChanged = (files, thumbs) => {
        setFiles(files);
        setThumbs(thumbs);
    };

    const handleSubmit = (values) => {
        if (!values.name) {
            toast.error('Vpiši ime izletne točke.');
        } else if (!values.type_id) {
            toast.error('Izberi tip izletne točke.');
        } else if (!values.location.lat || !values.location.lng) {
            toast.error('Določi lokacijo izletne točke.');
        } else {
            onSubmit(values, files);
        }
    };

    const handleModalCoordinatesSubmit = (coordinates) => {
        setModalCoordinates(coordinates);
        setIsCoordinatesModalActive(false);
    };

    const poiTypeOptions = useMemo(
        () =>
            poiTypes?.data?.map((pt) => ({
                value: pt.id,
                label: <PoiTypeOptionLabel slug={pt.slug} name={pt.name} />
            })),
        [poiTypes]
    );

    return (
        <div className="max-w-2xl mx-auto">
            <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
                {({ errors, handleChange, handleSubmit, touched, values, setFieldValue }) => (
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div>
                            <div className="mb-12 text-center">
                                <h1 className="text-brand">Predlagaj novo izletno točko</h1>
                                <p className="mt-4 text-lg">
                                    Hvala, da pomagaš soustvarjati največjo zbirko izletniških idej v Sloveniji.
                                    {contestState === 'ACTIVE' ? (
                                        <>
                                            {' '}
                                            S fotografijami, ki jih naložiš na naš portal, lahko osvojiš fotoaparat{' '}
                                            <span className="font-medium text-brand">
                                                {' '}
                                                Nikon z 30 ali eno od 15 majic Kam za vikend
                                            </span>
                                            .{' '}
                                        </>
                                    ) : (
                                        <>
                                            {' '}
                                            Z dodano novo izletno točko, opisom in fotografijami pomagaš vsem
                                            pustolovcem, ki jim bo tvoj vnos v veliko pomoč.{' '}
                                        </>
                                    )}
                                </p>
                            </div>
                            <div className="form-section mt-4">
                                <h3 className="mb-6">Vpiši ime in izberi tip izletne točke</h3>
                                <div className="flex gap-6 flex-col md:flex-row">
                                    <div className="sm:w-2/3">
                                        <FormikInputField
                                            label="Ime izletne točke"
                                            value={values.name}
                                            type="text"
                                            name="name"
                                            placeholder="Vnesi ime izletne točke ..."
                                        />
                                    </div>
                                    <div className="sm:w-1/3">
                                        <SelectInput
                                            options={poiTypeOptions || []}
                                            value={values.type_id}
                                            label="Tip izletne točke"
                                            placeholder="Izberi tip izletne točke ..."
                                            handleChange={(value) => setFieldValue('type_id', value)}
                                            error={Boolean(touched.type_id && errors.type_id)}
                                            helperText={touched.type_id && errors.type_id}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="form-section mt-8">
                                <h3 className="mb-2">Določi lokacijo</h3>
                                <div className="flex gap-4 sm:gap-28 flex-col md:flex-row">
                                    <p className="flex-grow">
                                        Poišči lokacijo ali pa jo ročno določi s klikom na zemljevid.
                                    </p>
                                    <div className="flex-shrink-0">
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => setIsCoordinatesModalActive(true)}
                                        >
                                            Vnesi koordinate
                                        </Button>
                                    </div>
                                </div>
                                <div className="h-72 sm:h-96 w-full mt-4 overflow-hidden rounded-xl">
                                    <MapLocationPicker
                                        popupLabel={values.name}
                                        coordinates={modalCoordinates}
                                        initialCoordinates={values.location}
                                        onCoordinatesChange={(coordinates) => setFieldValue('location', coordinates)}
                                    />
                                </div>
                            </div>
                            <div className="form-section mt-8">
                                <h3 className="mb-2">Naloži fotografije</h3>
                                <PhotoUploader
                                    handleFilesChange={filesChanged}
                                    maxPhotos={10}
                                    initialFiles={files}
                                    initialThumbs={thumbs}
                                    enableDescriptions
                                />
                                {files.length === 0 && (
                                    <div className="mt-2">
                                        <Checkbox
                                            label="Nimam fotografij"
                                            name="no_photos"
                                            isChecked={values.no_photos}
                                            onChange={handleChange}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 text-right">
                                <Button endIcon={<Send />} type={'submit'}>
                                    Pošlji
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </Formik>
            <CoordinatesModal
                isOpen={isCoordinatesModalActive}
                handleClose={() => setIsCoordinatesModalActive(false)}
                handleSubmit={handleModalCoordinatesSubmit}
            />
        </div>
    );
}

export default PoiSuggestionForm;
